import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { AgentSummary, Agent } from '../types/agent.js';
import * as agentApi from './agent-api.js';
import { wsClient } from './websocket-client.js';

export type AgentLoadingState = 'idle' | 'loading' | 'loaded' | 'error';

let sharedAgents: AgentSummary[] = [];
let sharedActiveAgent: Agent | null = null;
let sharedLoadingState: AgentLoadingState = 'idle';
let sharedError: string | null = null;

const hosts = new Set<ReactiveControllerHost>();

function notifyHosts(): void {
  for (const host of hosts) {
    host.requestUpdate();
  }
}

async function loadAgents(): Promise<void> {
  if (sharedLoadingState === 'loading') return;
  sharedLoadingState = 'loading';
  sharedError = null;
  notifyHosts();

  try {
    sharedAgents = await agentApi.getAgents();
    sharedLoadingState = 'loaded';
  } catch (e) {
    sharedLoadingState = 'error';
    sharedError = e instanceof Error ? e.message : 'Agenten konnten nicht geladen werden';
  }

  notifyHosts();
}

function onWsMessage(e: Event): void {
  const detail = (e as CustomEvent).detail as { type: string };
  const type = detail.type;

  if (type === 'agent:created' || type === 'agent:updated' || type === 'agent:deleted') {
    void loadAgents();
  }
}

// Singleton listener: intentionally never removed. Agent state is global and persistent.
wsClient.addEventListener('ws-message', onWsMessage);

export class AgentStateController implements ReactiveController {
  private host: ReactiveControllerHost;

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void {
    hosts.add(this.host);
  }

  hostDisconnected(): void {
    hosts.delete(this.host);
  }

  get agents(): AgentSummary[] {
    return sharedAgents;
  }

  get activeAgent(): Agent | null {
    return sharedActiveAgent;
  }

  get loadingState(): AgentLoadingState {
    return sharedLoadingState;
  }

  get error(): string | null {
    return sharedError;
  }

  async loadAll(): Promise<void> {
    await loadAgents();
  }

  async loadOne(id: number): Promise<void> {
    try {
      sharedActiveAgent = await agentApi.getAgent(id);
      notifyHosts();
    } catch (e) {
      sharedError = e instanceof Error ? e.message : 'Agent konnte nicht geladen werden';
      notifyHosts();
    }
  }

  async refresh(): Promise<void> {
    await loadAgents();
  }
}
